import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  return new Date(timestamp).toLocaleString();
};

export default function AdminAuditLogTab({
  auditFilters,
  setAuditFilters,
  actionTypes,
  adminsList,
  auditLogs,
  fetchData,
}) {
  return (
    <div data-testid="audit-log-tab-content">
      <div className="mb-6">
        <h2 className="font-display text-2xl text-navy mb-1">Audit Log</h2>
        <p className="text-text-mid text-sm">Track all admin actions and system changes</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <Select
            value={auditFilters.action}
            onValueChange={(v) => setAuditFilters({ ...auditFilters, action: v === 'all' ? '' : v })}
          >
            <SelectTrigger className="w-[200px] rounded-xl">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actionTypes.map((action) => (
                <SelectItem key={action} value={action}>{action.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={auditFilters.adminId}
            onValueChange={(v) => setAuditFilters({ ...auditFilters, adminId: v === 'all' ? '' : v })}
          >
            <SelectTrigger className="w-[200px] rounded-xl">
              <SelectValue placeholder="Filter by admin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Admins</SelectItem>
              {adminsList.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              setAuditFilters({ action: '', adminId: '' });
              fetchData();
            }}
            className="rounded-xl"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-text-mid">
                  No audit logs found
                </TableCell>
              </TableRow>
            ) : (
              auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-text-light" />
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{log.admin_email}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        log.action.includes('LOGIN') ? 'border-green-300 text-green-700' :
                        log.action.includes('REVOKED') || log.action.includes('LOCKED') ? 'border-red-300 text-red-700' :
                        log.action.includes('INVITED') || log.action.includes('SETUP') ? 'border-blue-300 text-blue-700' :
                        'border-gray-300 text-gray-700'
                      }
                    >
                      {log.action.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {log.details && Object.keys(log.details).length > 0 ? (
                      <div className="text-xs text-text-mid max-w-xs truncate">
                        {Object.entries(log.details).map(([key, value]) => (
                          <span key={key} className="mr-2">
                            <span className="font-medium">{key}:</span> {String(value)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-text-light text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
